import { ACTToolbarService } from 'src/app/angularJS/services/toolbar.service';

import { UserService } from 'src/app/angularJS/services/user/user.service';

import { UserMessagesService } from 'src/app/angularJS/services/user-messages.service';

import { KeycloakService } from 'src/app/angularJS/services/keycloak.service';

/**
 * A controller that contains properties and methods relating to the applications toolbar
 */
export class ToolbarController {
  public static $inject = [
    '$scope',
    '$mdSidenav',
    '$translate',
    'ACTToolbarService',
    '$mdComponentRegistry',
    '$state',
    'userService',
    'UserMessages',
    'keycloakService',
    '$location',
    '$window',
    '$transitions'
  ];
  public actions: Array<any>;
  public showSearch: boolean;
  public showTabs: boolean;
  public currentTab: number;
  public tabsConfig: Array<InterACT.Interfaces.ITabConfig>;
  public searchPlaceholderText: string;
  public clearSearch: Function;
  public search: Function;
  public searchTerm: string;
  public showAdvancedSearch: boolean;
  public advancedSearch: Function;
  public pageTitle: string;
  public selectedSchemeName: string;
  public organisationName: string;
  public isNavOpen: Function;
  public showCustomTabs: boolean;

  private name: string;
  private currentLocale: string;

  constructor(
    private $scope,
    private $mdSidenav,
    private $translate: ng.translate.ITranslateService,
    private ActToolbarService: ACTToolbarService,
    private $mdComponentRegistry,
    public $state: ng.ui.IStateService,
    private userService: UserService,
    private userMessages: UserMessagesService,
    private keycloakService: KeycloakService,
    private $location: ng.ILocationService,
    private $window: ng.IWindowService,
    private $transitions
  ) {
    this.initialiseToolbar();
  }

  /**
   * A method that logs the user out of the application
   */
  public logout() {
    this.userMessages.buildConfirmDialog(
      'CONFIRM_DIALOG.LOGOUT.TITLE',
      'CONFIRM_DIALOG.LOGOUT.MESSAGE',
      {},
      'CONFIRM_DIALOG.LOGOUT.CONFIRM',
      'CONFIRM_DIALOG.LOGOUT.CANCEL'
    ).then(() => {
      this.userService.clearSelectedSchemes();
      const urlParts = this.$location.absUrl().split('/#/');
      let redirectUrl = undefined;

      if (urlParts.length > 0) {
        redirectUrl = urlParts[0] + '/#/';
      }

      this.keycloakService.logout({ redirectUri: redirectUrl });
    });
  }

  /**
   * A method that returns the user initials concatenated together as uppercase
   *
   * @returns the user initials concatenated together as uppercase
   */
  public getUserInitials() {
    return (this.userService.userDetails.firstname[0] + this.userService.userDetails.lastName[0]).toUpperCase();
  }

  /**
   * A method that handles the keypress event on the search input
   */
  public onSearchInputKeypress($event: KeyboardEvent) {
    if ($event.which === 13 && this.searchTerm !== '') {
      this.search(this.searchTerm);
    }
  }

  /**
   * Sets the tab details using the selected tab
   * @param tabsConfig the config of tabs
   * @param selectedTab the currently selected tab
   */
  public setTabs(tabsConfig: Array<InterACT.Interfaces.ITabConfig>, selectedTab?: number, showCustomTabs?: boolean) {
    if (this.hasValidTabsConfig(tabsConfig, showCustomTabs)) {
      this.tabsConfig = tabsConfig;
      this.showTabs = true;
      this.currentTab = selectedTab;
      this.showCustomTabs = showCustomTabs ? showCustomTabs : false;
    }
  }

  /**
   * Sets the current tab
   * @param tab the tab number
   */
  public setActiveTab = (tab: number) => {
    this.currentTab = tab;
  }

  /**
   * Sets search functions and display values and sets up watch on search term
   * @param placeholderText the placeholder for the search text
   * @param performSearch a function to run on calling search
   * @param clearSearch a function to run on calling clearSearch
   * @param advancedSearch a function to run on calling advancedSearch
   */
  public setSearch(
    placeholderText: string,
    performSearch?: Function,
    clearSearch?: Function,
    advancedSearch?: Function
  ) {
    this.searchPlaceholderText = placeholderText;
    this.search = performSearch;
    this.advancedSearch = advancedSearch;
    this.showSearch = true;
    this.showAdvancedSearch = angular.isDefined(advancedSearch);
    this.clearSearch = clearSearch;

    this.$scope.$watch(() => this.searchTerm, (term) => {
      if (!term || term.length === 0) {
        this.clearSearch();
      } else if (term.length > 3) {
        this.search(term);
      }
    });
  }

  /**
   * Sets the default features of the toolbar
   * @param title the title of the toolbar
   * @param actions the extra actions to be displayed in the more menu
   * @param breadcrumbs the breadcrumbs to be displayed
   */
  public setDefaults(title: string, actions?: Array<any>) {
    this.pageTitle = title;

    if (angular.isUndefined(actions)) {
      this.actions = [];
    } else {
      this.actions = actions;
    }
    this.showTabs = false;
    this.showCustomTabs = false;
  }

  /**
   * Opens the navbar
   */
  public openNavbar() {
    this.$mdSidenav('left').open();
  }

  /**
   * Sets the more menu actions
   * @param actionsArray the extra actions to be displayed in the more menu
   */
  public setActions(actionsArray: Array<any>) {
    this.actions = actionsArray;
  }

  /**
   * Sets the title of the toolbar
   * @param content the title content of the toolbar
   * @param variables any variables to be applied to the translation of the title content
   */
  public setTitle(content: string, variables?: Object) {
    const contentArray = content.split(' ');
    for (let i = 0; i < contentArray.length; i++) {
      contentArray[i] = this.$translate.instant(contentArray[i], variables);
    }
    content = contentArray.join(' ');
    this.pageTitle = content;
    this.showTabs = false;
    this.showCustomTabs = false;
  }

  /**
   * Sets the title of the toolbar
   * @param title the title of the toolbar
   */
  public setPageTitle(title: string) {
    this.pageTitle = title;
  }

  /**
   * Clears the searchTerm and calls clearSearch function
   */
  public clearSearchField() {
    if (angular.isDefined(this.clearSearch)) {
      this.clearSearch();
    }
    this.searchTerm = '';
  }

  /**
   * Determins if the entered tabsConfig is valid
   * @returns true if valid
   */
  public hasValidTabsConfig(tabsConfig: Array<InterACT.Interfaces.ITabConfig>, showCustomTabs?: boolean) {
    let tabsConfigIsValid = true;
    angular.forEach(tabsConfig, (value) => {
      if (!showCustomTabs) {
        if (!angular.isObject(value) || !value.hasOwnProperty('label') || !value.hasOwnProperty('sref')) {
          tabsConfigIsValid = false;
        }
      } else {
        if (!angular.isObject(value) || !value.hasOwnProperty('label')) {
          tabsConfigIsValid = false;
        }
      }
    });
    return tabsConfigIsValid;
  }

  /**
   * Determins if the tab entered has an sref matching the current state
   * @param tab the tab to check
   * @returns true if the $state contains the sref of the tab entered
   */
  public isTabActive = (tab: InterACT.Interfaces.ITabConfig) => {
    //check to see if the sref for this tab contains parameters
    let sRefWithoutParameters = '';

    if (tab.sref.indexOf('(') !== -1) {
      sRefWithoutParameters = tab.sref.split('(')[0];
    } else {
      sRefWithoutParameters = tab.sref;
    }

    return this.$state.includes(sRefWithoutParameters);
  }

  /**
   * Gets the selected scheme's name from the user service and assigns it to selectedSchemeName
   */
  public updateScheme = () => {
    if (this.userService.selectedSchemes.length === 0) {
      this.selectedSchemeName = '';
      return;
    }

    if (this.userService.selectedSchemes.length > 1) {
      this.selectedSchemeName = this.$translate.instant('MULTIPLE_SCHEMES');
    } else if (this.userService.selectedSchemes[0]) {
      this.selectedSchemeName = this.userService.selectedSchemes[0].name[this.currentLocale];
    }
  }

  /**
   * Redirects to the keycloak account management screen
   */
  public manageProfile = () => {
    this.keycloakService.goToAccountManagement();
  }

  /**
   * Determines whether the user has sufficient roles to manage other user accounts
   */
  public canManageUsers = () => {
    return this.keycloakService.canManageUsers();
  }

  /**
   * Redirects to the keycloak admin console screen
   */
  public manageUsers = () => {
    this.$window.location.href = this.keycloakService.createAdminConsoleUrl();
  }

  public shouldShowMenuItem(uiRole: string): Boolean {
    const menuItemPermissionDetails: InterACT.Interfaces.IUIPermissionDetails = this.userService.permissions['ui'][uiRole];
    return menuItemPermissionDetails ? menuItemPermissionDetails.canAccess : false;
  }

  private getOrganisationName = () => {
    this.userService.getUserOrganisation().then((organisationName: string) => {
      this.organisationName = organisationName;
    });
  }

  private initialiseToolbar() {
    this.currentLocale = this.$translate.use();

    this.updateScheme();

    this.getOrganisationName();

    this.userService.registerSchemeCallback(this.updateScheme);

    this.showSearch = false;
    this.ActToolbarService.register(this);

    this.$transitions.onStart({}, () => {
      this.showSearch = false;
      this.clearSearchField();
    });

    this.name = 'primary';
    const componentRegistration = this.$mdComponentRegistry.register(this, this.name);
    this.$mdComponentRegistry.when('left').then(() => {
      this.isNavOpen = this.navbarOpenCheck;
    });

    this.$scope.$on('$destroy', () => {
      this.ActToolbarService.unRegister(this.name);
      componentRegistration();
    });
  }

  /**
  * Determines whether the navbar is open
  * @returns true if the navbar is open
 */
  private navbarOpenCheck = () => {
    return this.$mdSidenav('left').isOpen();
  }
}
